import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";

export const QA_ANDROID_PACKAGE = "ru.axonleads.app.qa";
const API_BASE_URL = (
  import.meta.env.VITE_AXON_API_BASE_URL
  || (import.meta.env.DEV ? "http://127.0.0.1:8000" : "https://api.axonleads.ru")
).replace(/\/$/, "");

export function isQaPackage(platform, isNative, appId) {
  return isNative && platform === "android" && appId === QA_ANDROID_PACKAGE;
}

export async function isQaAndroidApp() {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") return false;
  try {
    const info = await CapacitorApp.getInfo();
    return isQaPackage(Capacitor.getPlatform(), true, info.id);
  } catch {
    return false;
  }
}

export function qaPushTapPath(data) {
  return data?.kind === "axon_qa_push_v1" && data?.route === "/today" ? "/today" : null;
}

async function qaRequest(idToken, method, body) {
  const response = await fetch(`${API_BASE_URL}/v1/notifications/qa-device`, {
    method,
    headers: {
      Authorization: `Bearer ${idToken}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    cache: "no-store",
    credentials: "omit",
    redirect: "error",
    referrerPolicy: "no-referrer",
  });
  if (!response.ok) {
    throw new Error(response.status === 403
      ? "Тестовые уведомления доступны только QA-аккаунту."
      : method === "GET"
        ? "Не удалось проверить QA-статус. Попробуйте позже."
        : "Не удалось подключить тестовое уведомление. Попробуйте позже.");
  }
  return response.json();
}

export async function fetchQaPushStatus(idToken) {
  if (!(await isQaAndroidApp())) {
    throw new Error("Это действие доступно только в приложении AxonLeads QA.");
  }
  return qaRequest(idToken, "GET");
}

export async function registerWithNativePlugin(plugin = PushNotifications) {
  let permission = await plugin.checkPermissions();
  if (permission.receive === "prompt" || permission.receive === "prompt-with-rationale") {
    permission = await plugin.requestPermissions();
  }
  if (permission.receive !== "granted") {
    throw new Error("Уведомления не разрешены на устройстве. Включите их в настройках Android.");
  }

  let resolved = false;
  let resolveToken;
  let rejectToken;
  const tokenReady = new Promise((resolve, reject) => {
    resolveToken = resolve;
    rejectToken = reject;
  });
  const registration = await plugin.addListener("registration", ({ value }) => {
    if (!resolved) {
      resolved = true;
      resolveToken(value);
    }
  });
  let registrationError;
  try {
    registrationError = await plugin.addListener("registrationError", () => {
      if (!resolved) {
        resolved = true;
        rejectToken(new Error("Android не смог зарегистрировать тестовое уведомление."));
      }
    });
    await plugin.register();
    return await Promise.race([
      tokenReady,
      new Promise((_, reject) => setTimeout(() => reject(new Error("Android не ответил вовремя.")), 15000)),
    ]);
  } finally {
    await registration.remove();
    if (registrationError) await registrationError.remove();
  }
}

export async function enableQaPush(idToken) {
  if (!(await isQaAndroidApp())) {
    throw new Error("Это действие доступно только в приложении AxonLeads QA.");
  }
  try {
    // Permission is requested only from the explicit settings button.
    const token = await registerWithNativePlugin();
    return await qaRequest(idToken, "POST", { packageName: QA_ANDROID_PACKAGE, token });
  } catch (error) {
    await PushNotifications.unregister().catch(() => {});
    throw error;
  }
}

export async function disableQaPush(idToken) {
  if (!(await isQaAndroidApp())) return;
  try {
    if (idToken) await qaRequest(idToken, "DELETE");
  } finally {
    await PushNotifications.unregister().catch(() => {});
  }
}

export async function unregisterQaNativePush() {
  if (await isQaAndroidApp()) await PushNotifications.unregister().catch(() => {});
}

export async function addQaPushTapListener(navigate) {
  if (!(await isQaAndroidApp())) return null;
  return PushNotifications.addListener("pushNotificationActionPerformed", (event) => {
    const path = qaPushTapPath(event?.notification?.data);
    if (path) navigate(path);
  });
}
